import {useEffect, useRef} from 'react'
import {useDispatch} from 'react-redux'
import {Link, useNavigate, useSearchParams} from 'react-router'
import {toast} from 'react-toastify'
import {verifyPasswordResetGoogle} from 'setup/redux/Actions/SignIn/forgotPassword'

import {toAbsoluteUrl} from '_metronic/helpers'

import {
  clearPendingGooglePasswordReset,
  storePendingGooglePasswordReset,
} from '../selective/forgotSetPasswordStorage'
import {isPasswordResetConflict} from '../selective/passwordResetConflict'
import {
  getPasswordResetVerifyError,
  handleVerifyPasswordResetSuccess,
} from '../selective/passwordResetHelpers'
import {VerifyPasswordResetResult} from '../selective/types'

/**
 * Receives Google credential from central auth domain after user chooses
 * "Continue with Google" on the forgot/set password page.
 */
export function GooglePasswordResetCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const exchangeStarted = useRef(false)

  const credential = searchParams.get('credential')

  useEffect(() => {
    if (!credential) {
      toast.error('No Google credential received')
      navigate('/auth/forgot-password', {replace: true})
      return
    }

    if (exchangeStarted.current) return
    exchangeStarted.current = true

    storePendingGooglePasswordReset(credential)

    dispatch(
      verifyPasswordResetGoogle(
        credential,
        null,
        (res: {result?: VerifyPasswordResetResult}) => {
          clearPendingGooglePasswordReset()
          if (!handleVerifyPasswordResetSuccess(navigate, res?.result)) {
            toast.error('Could not verify Google sign-in for password reset')
            navigate('/auth/forgot-password', {replace: true})
          }
        },
        (err: unknown) => {
          if (isPasswordResetConflict(err as Parameters<typeof isPasswordResetConflict>[0])) {
            const conflict = err as {result: {instances: unknown[]}}
            navigate('/auth/forgot-password', {
              replace: true,
              state: {
                googleIdToken: credential,
                conflictInstances: conflict.result.instances,
              },
            })
            return
          }
          clearPendingGooglePasswordReset()
          const message = getPasswordResetVerifyError(
            err as {statusCode?: number; message?: string},
            'google'
          )
          toast.error(message)
          navigate('/auth/forgot-password', {replace: true})
        }
      )
    )
  }, [credential, dispatch, navigate])

  return (
    <div
      className='d-flex flex-column justify-content-center align-items-center min-vh-100 p-10'
      style={{background: 'linear-gradient(90.14deg, #431214 0.12%, #031C36 99.87%)'}}
    >
      <img
        src={toAbsoluteUrl('/media/logos/LogoAnim1.gif')}
        alt='Loading'
        style={{width: 88, height: 88, objectFit: 'contain'}}
      />
      <p className='text-white fw-semibold mt-4 mb-0'>Verifying Google sign-in…</p>
      <Link to='/auth/forgot-password' className='text-white-50 mt-6 fs-7'>
        Cancel
      </Link>
    </div>
  )
}
